import '@aus-platform/design-system/dist/style.css';
import './assets/scss/main.scss';

// Overriding.
import './overrides';

import { Spinner } from '@aus-platform/design-system';
import { lazy, Suspense, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import type { LoggedUser } from 'shared/api';
import ProtectedRoutes, {
  AnalyticsSessionTracker,
  NotFoundPage,
} from 'shared/components';
import AuthRoutes from 'shared/components/auth-route';
import { history } from 'shared/constants/history';
import { GlobalContext } from 'shared/context';
import { RoutesEnum } from 'shared/routes';
import { SSOAuthorizationFailed } from './auth/sso-auth/sso-authorization-failed';
import { Home } from './home';
import { PrivacyPolicy } from './privacy-policy';

const Users = lazy(() => import('./users'));
const Projects = lazy(() => import('./projects'));
const Processing = lazy(() => import('./processing'));
const Datasets = lazy(() => import('./datasets'));
const DatasetResources = lazy(() => import('./dataset-resources'));
const Logout = lazy(() => import('./auth/logout'));
const Login = lazy(() => import('./auth/login'));
const MicrosoftAuthRedirect = lazy(
  () => import('./auth/login/microsoft-auth-redirect'),
);
const AccessTags = lazy(() => import('./access-tags'));
const PlanVsActual = lazy(() => import('./dashboard'));
const Productivity = lazy(() => import('./dashboard/productivity'));
const VolumeDashboard = lazy(
  () => import('./dashboard/productivity/volume-dashboard'),
);
const LeadDistanceDashboard = lazy(
  () => import('./dashboard/productivity/lead-distance-dashboard'),
);
const ProductivityFiles = lazy(() => import('./dashboard/productivity/files'));
const GeotagGeneration = lazy(
  () => import('./processing-workflow/geotag-generation'),
);

const Reports = lazy(() => import('./reports'));
const UserGroups = lazy(() => import('./user-groups'));
const Map3D = lazy(() => import('./map-3d'));
const SwipeMap = lazy(() => import('./swipe-map'));
const SwipeMap3D = lazy(() => import('./swipe-map-3d'));
const Sites = lazy(() => import('./sites'));
const TwoFactorVerification = lazy(
  () => import('./auth/two-factor-authentication/login-verification'),
);
const TwoFactorSetup = lazy(
  () => import('./auth/two-factor-authentication/two-factor-setup-flow'),
);
const LoginChangePassword = lazy(() => import('./auth/change-password'));

export const App = () => {
  // States.
  const [loggedUser, setLoggedUser] = useState<LoggedUser | null>(null);

  // Render.
  return (
    <div className="app">
      <GlobalContext.Provider value={{ loggedUser, setLoggedUser }}>
        <AnalyticsSessionTracker />
        <Suspense fallback={<Spinner />}>
          <Routes>
            <Route
              path={RoutesEnum.PrivacyPolicy}
              element={<PrivacyPolicy />}
            />
            <Route element={<AuthRoutes />}>
              <Route
                path={RoutesEnum.Login}
                element={<Login history={history} />}
              />
            </Route>
            <Route
              path={RoutesEnum.MicrosoftAuthRedirect}
              element={<MicrosoftAuthRedirect />}
            />

            <Route
              path={RoutesEnum.TwoFactorVerification}
              element={<TwoFactorVerification />}
            />

            <Route
              path={RoutesEnum.TwoFactorSetup}
              element={<TwoFactorSetup />}
            />

            <Route
              path={RoutesEnum.ChangePassword}
              element={<LoginChangePassword />}
            />

            <Route element={<ProtectedRoutes />}>
              <Route
                path={RoutesEnum.Home}
                element={<Navigate to={RoutesEnum.Map3D} />}
              />
              <Route path={RoutesEnum.Logout} element={<Logout />} />
              <Route path={RoutesEnum.Home} element={<Home />}>
                <Route path={RoutesEnum.Users} element={<Users />} />
                <Route path={RoutesEnum.UserGroups} element={<UserGroups />} />
                <Route path={RoutesEnum.AccessTags} element={<AccessTags />} />
                <Route path={RoutesEnum.Projects} element={<Projects />} />
                <Route path={RoutesEnum.Sites} element={<Sites />} />
                <Route path={RoutesEnum.Iterations} element={<Datasets />} />
                <Route path={RoutesEnum.MergedDataset} element={<Datasets />} />
                <Route
                  path={RoutesEnum.Layers}
                  element={<DatasetResources />}
                />
                <Route path={RoutesEnum.Tasks} element={<DatasetResources />} />
                <Route
                  path={RoutesEnum.GeotagGeneration}
                  element={<GeotagGeneration />}
                />
                <Route
                  path={RoutesEnum.Storage}
                  element={<DatasetResources />}
                />
                <Route path={RoutesEnum.Map3DWorkspace} element={<Map3D />} />
                <Route path={RoutesEnum.Map3D} element={<Map3D />} />
                <Route path={RoutesEnum.SwipeMap} element={<SwipeMap />} />
                <Route path={RoutesEnum.SwipeMap3D} element={<SwipeMap3D />} />
                <Route path={RoutesEnum.Dashboard}>
                  <Route
                    index
                    element={
                      <Navigate
                        to={`${RoutesEnum.DashboardProductivity}/${RoutesEnum.DashboardProductivityVolume}`}
                        replace
                      />
                    }
                  />

                  <Route
                    path={RoutesEnum.DashboardProductivity}
                    element={<Productivity />}
                  >
                    <Route
                      index
                      element={
                        <Navigate
                          to={RoutesEnum.DashboardProductivityVolume}
                          replace
                        />
                      }
                    />

                    <Route
                      path={RoutesEnum.DashboardProductivityVolume}
                      element={<VolumeDashboard />}
                    />

                    <Route
                      path={RoutesEnum.DashboardProductivityLeadDistance}
                      element={<LeadDistanceDashboard />}
                    />

                    <Route
                      path={RoutesEnum.DashboardProductivityFiles}
                      element={<ProductivityFiles />}
                    />
                  </Route>

                  <Route
                    path={RoutesEnum.DashboardPlanVsActual}
                    element={<PlanVsActual />}
                  />
                </Route>
                <Route path={RoutesEnum.Reports} element={<Reports />} />
                <Route
                  path={RoutesEnum.ProcessingRoutes}
                  element={<Processing />}
                />
              </Route>
            </Route>
            <Route
              path="*"
              element={<Navigate to={RoutesEnum.PageNotFound} />}
            />

            <Route path={RoutesEnum.PageNotFound} element={<NotFoundPage />} />
            <Route
              path={RoutesEnum.UnAuthorizedSSO}
              element={<SSOAuthorizationFailed />}
            />
          </Routes>
        </Suspense>
      </GlobalContext.Provider>
    </div>
  );
};
